import { EncryptionKeyEnum } from '../constants/encryption-key.ts';

export class EncryptionUtil {
  private key: CryptoKey | undefined;
  constructor(
    private readonly secretKey: string,
    private readonly salt: string = EncryptionKeyEnum.DEFAULT_SALT
  ) {}

  private async getKey(): Promise<CryptoKey> {
    if (this.key) {
      return this.key;
    }

    const enc = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      enc.encode(this.secretKey),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    const key = await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: enc.encode(this.salt),
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );

    this.key = key;
    return key;
  }

  async encrypt(data: unknown): Promise<string> {
    const key = await this.getKey();
    const serializedData = typeof data === 'string' ? data : JSON.stringify(data);

    const iv = window.crypto.getRandomValues(new Uint8Array(12)); // Initialization vector
    const enc = new TextEncoder();
    const encryptedData = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      enc.encode(serializedData)
    );

    const buffer = new Uint8Array(encryptedData);
    const combined = new Uint8Array(iv.byteLength + buffer.byteLength);
    combined.set(iv, 0);
    combined.set(buffer, iv.byteLength);

    let binary = '';

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < combined.length; i++) {
      binary += String.fromCharCode(combined[i]);
    }
    return btoa(binary);
  }

  async decrypt(data: unknown): Promise<string> {
    const key = await this.getKey();
    const binaryString = atob(data as string);
    const combined = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      combined[i] = binaryString.charCodeAt(i);
    }

    // Extract the IV and the encrypted data
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    const decryptedData = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      encrypted
    );

    const dec = new TextDecoder();
    return dec.decode(decryptedData);
  }
}
